import { useState, useEffect, useRef } from "react";
import { useNotification } from "../context/NotificationContext";
import { useAuth } from "../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import Settings from "../features/settings/SettingsModal";
import Loader from "./Loader";
import Browser from "./ui/Browser";
import Profile from "./ui/Profile";
import {
 Bell,
 ChevronDown,
 ChevronLeft,
 Menu,
 X,
 CheckCircle2,
 AlertCircle,
 Info,
 XCircle
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

export default function Navbar({ isSidebarOpen, setIsSidebarOpen }) {
 const { logout, user } = useAuth();
 const navigate = useNavigate();
 const { history, clearHistory } = useNotification();

 const [loading] = useState(false);
 const [notifOpen, setNotifOpen] = useState(false);
 const [menuOpen, setMenuOpen] = useState(false);

 const notifRef = useRef(null);
 const profileRef = useRef(null);

 useEffect(() => {
   const handleClickOutside = (event) => {
     if (notifRef.current && !notifRef.current.contains(event.target)) {
       setNotifOpen(false);
     }
     if (profileRef.current && !profileRef.current.contains(event.target)) {
       setMenuOpen(false);
     }
   };
   document.addEventListener("mousedown", handleClickOutside);
   return () => {
     document.removeEventListener("mousedown", handleClickOutside);
   };
 }, []);

 const handleLogout = () => {
 logout();
 navigate("/");
 };

 return (
 <>
 {loading ? (
 <Loader />
 ) : (
 <header className="relative w-full bg-surface shadow-sm z-40">
 <div className="flex justify-between items-center px-4 py-2">
 {/* 🔹 Gauche - Menu + Search */}
 <div className="flex items-center gap-4">
 <button
 type="button"
 onClick={() => setIsSidebarOpen(!isSidebarOpen)}
 aria-controls="workspace-sidebar"
 aria-expanded={isSidebarOpen}
 aria-label={isSidebarOpen ? "Masquer le menu latéral" : "Afficher le menu latéral"}
 className="rounded-md p-2 transition hover:bg-surface-muted focus-hp"
 >
 {isSidebarOpen ? (
 <ChevronLeft aria-hidden="true" className="h-6 w-6 text-muted" />
 ) : (
 <Menu aria-hidden="true" className="h-6 w-6 text-muted" />
 )}
 </button>

 {/* 🔍 Search / Browser */}
 <div className="shrink-0">
 <Browser />
 </div>
 </div>

 {/* 🔹 Droite - Notifications, Settings, Profile */}
 <div className="flex items-center gap-4">

 {/* 🔔 Notifications */}
 <div className="relative" ref={notifRef}>
 <button
 onClick={() => setNotifOpen(!notifOpen)}
 className="relative p-2 rounded-full hover:bg-surface-muted transition"
 >
 <Bell className="w-6 h-6 text-muted" />
 {history.length > 0 && (
 <span className="absolute top-0 right-0 -mt-1 -mr-1 badge badge-danger min-w-[20px] h-5 flex items-center justify-center rounded-full">
 {history.length}
 </span>
 )}
 </button>

 {/* 🔻 Notifications dropdown */}
 <AnimatePresence>
 {notifOpen && (
 <motion.div
   initial={{ opacity: 0, y: -10, scale: 0.95 }}
   animate={{ opacity: 1, y: 0, scale: 1 }}
   exit={{ opacity: 0, y: -10, scale: 0.95 }}
   transition={{ duration: 0.2 }}
   className="absolute right-0 mt-2 w-80 bg-surface border border-workspace rounded-xl shadow-hp-xl z-[999] p-4"
 >
 <div className="flex justify-between items-center border-b border-workspace pb-2">
 <h3 className="font-semibold text-primary">Notifications</h3>
 <button
 onClick={clearHistory}
 className="text-sm text-danger hover:underline"
 >
 Effacer tout
 </button>
 </div>
 <div className="max-h-60 overflow-y-auto mt-2 space-y-1">
 {history.length > 0 ? (
 history.map((notif) => {
 let Icon = Info;
 let color = "text-secondary";
 if (notif.type === "success") { Icon = CheckCircle2; color = "text-success-500"; }
 if (notif.type === "error") { Icon = XCircle; color = "text-danger-500"; }
 if (notif.type === "warning") { Icon = AlertCircle; color = "text-orange-500"; }
 
 return (
 <div key={notif.id} className="flex items-start gap-2 p-2 border-b border-workspace last:border-0 hover:bg-workspace transition-colors rounded-lg">
 <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
 <div className="text-sm text-muted leading-tight flex-1">
 {notif.message}
 <span className="block text-[10px] text-subtle mt-1">
 {notif.date ? new Date(notif.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}
 </span>
 </div>
 </div>
 );
 })
 ) : (
 <p className="text-sm text-subtle text-center py-4">Aucune notification</p>
 )}
 </div>
 </motion.div>
 )}
 </AnimatePresence>
 </div>

 {/* ⚙️ Settings */}
 <Settings />

 {/* 👤 Profil utilisateur */}
 <div className="relative" ref={profileRef}>
 <button
 onClick={() => setMenuOpen(!menuOpen)}
 className="flex items-center gap-2 bg-surface-muted px-2 py-1 rounded-full transition hover:shadow-hp-md border border-workspace"
 >
 <img
 src={user?.profileImage || '/assets/logo-hipparchus.png'} onError={(e) => { e.target.onerror = null; e.target.src = '/assets/logo-hipparchus.png'; }}
 alt="'/assets/logo-hipparchus.png'"
 className="w-8 h-8 rounded-full object-cover"
 />
 <div className="hidden md:block text-left">
 <p className="text-sm font-semibold text-primary">
 {user?.Nom || "Chargement..."}
 </p>
 <p className="text-xs text-subtle">{user?.jobTitle || user?.Rôle || user?.roleNames?.[0] || "Utilisateur"}</p>
 </div>
 <ChevronDown className="w-4 h-4 text-muted" />
 </button>

 {/* 🔻 Menu utilisateur */}
 <AnimatePresence>
 {menuOpen && (
 <motion.div
   initial={{ opacity: 0, y: -10, scale: 0.95 }}
   animate={{ opacity: 1, y: 0, scale: 1 }}
   exit={{ opacity: 0, y: -10, scale: 0.95 }}
   transition={{ duration: 0.2 }}
   className="absolute right-0 mt-2 w-72 sm:w-80 max-w-[calc(100vw-2rem)] bg-surface border border-workspace rounded-2xl shadow-hp-xl z-[999] overflow-hidden"
 >
 <div className="absolute top-3 right-3 z-10">
 <button 
   onClick={() => setMenuOpen(false)} 
   className="p-1 rounded-lg text-subtle hover:text-primary hover:bg-surface-muted transition"
   title="Fermer"
 >
 <X className="w-4 h-4" />
 </button>
 </div>
 <Profile user={user} onLogout={handleLogout} />
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 </div>
 </div>
 </header>
 )}
 </>
 );
}
